import axios from 'axios';
import { push } from 'connected-react-router';
import { isAfterNowByDays } from 'modules/utils/DateUtils';
import analyticsService, { AnalyticsEvent } from 'services/AnalyticsService';
import Config from 'react-native-config';
import { AnyAction, Dispatch } from 'redux';
import { getPathByComponentName } from 'screens/navigation';
import AuthServices from 'services/AuthServices';
import Logger from 'services/Logger';
import { apiKeySelector } from 'store/appSettings/selectors';
import { RootState, Thunk } from 'store/types';
import { oc } from 'ts-optchain';
import { DFD_AUTH_CONFIG } from './configs';
import { LOCAL_STORAGE_TOKEN } from './configs.web';
import { AuthResult, AuthResults } from './reducers';
import { dfdRefreshTokenSelector, isDfdAuthenticatedSelector } from './selectors';
import { authenticateTPS, isAuthenticatedWithSource } from './tpsHelpers';
import { toggleProtectedRouteModal } from './actions.common';

export interface StoredAuthInfo {
  dfdRefreshToken: string;
  lastLogin: string;
}

interface UserSession {
  accessToken: string;
  accessTokenExpirationDate: string;
  authorizeAdditionalParameters: any;
  tokenAdditionalParameters: any;
  additionalParameters: any;
  idToken: string;
  refreshToken: string;
  tokenType: string;
}
/**
 * Method: Singleton of dfdAuthService
 * Description: Auth Service Singleton to allow consistency between auth functions and to facilitate tests.
 */
let dfdAuthServiceSingelton: AuthServices;
export function getAuthService() {
  if (dfdAuthServiceSingelton) {
    return dfdAuthServiceSingelton;
  }
  return new AuthServices(DFD_AUTH_CONFIG);
}

export const setUserSession = (tokens: { accessToken: string }, apikey: string) => {
  return axios.post(
    `${Config.USS_BASE_URL}tokens`,
    { payload: tokens },
    {
      headers: { apikey, Authorization: `BEARER ${tokens.accessToken}` }
    }
  );
};

export const getUserSession = (token: string, apikey: string): Promise<UserSession> => {
  const config = {
    headers: { apikey, Authorization: `BEARER ${token}` }
  };

  return axios.get<UserSession>(`${Config.USS_BASE_URL}tokens`, config).then(res => res.data);
};

export const getStoredAuthInfo = () => {
  localStorage.getItem(LOCAL_STORAGE_TOKEN);
};

const removeTokens = () => {
  localStorage.removeItem(LOCAL_STORAGE_TOKEN);
};

const shouldForceLogin = (lastLogin: string): boolean => {
  return !lastLogin || !isAfterNowByDays(lastLogin, Config.AUTH_FORCE_LOGIN_DAYS);
};

export const AUTHENTICATE_SET_BLOCK_ROUTE = 'AUTH.AUTHENTICATE_SET_BLOCK_ROUTE';
export const setBlockRoute = (route: string | null) => ({
  type: AUTHENTICATE_SET_BLOCK_ROUTE,
  payload: route
});

const navigateToBlockedRoute = (dispatch: Dispatch<AnyAction>, state: RootState) => {
  const blockRoute = state.authentication.blockedRoute;
  if (blockRoute) {
    dispatch(setBlockRoute(null));
    dispatch(push(getPathByComponentName(blockRoute)));
  }
};

export const SET_TPS_AUTH_STATUS = 'AUTH.SET_TPS_AUTH_STATUS';
export const setAuthStatusTPS = (statuses: any) => ({
  type: SET_TPS_AUTH_STATUS,
  payload: statuses
});

export const AUTHENTICATE_USER = 'AUTH.AUTHENTICATE_USER';
export const AUTHENTICATE_USER_SUCCESS = 'AUTH.AUTHENTICATE_USER_SUCCESS';
export const AUTHENTICATE_USER_FAIL = 'AUTH.AUTHENTICATE_USER_FAIL';

export const authenticateUserSuccess = (auth: AuthResults): Thunk => (
  dispatch: Dispatch<AnyAction>,
  getState: () => RootState
) => {
  dispatch({ type: AUTHENTICATE_USER_SUCCESS, payload: auth });
  navigateToBlockedRoute(dispatch, getState());
};

export const authenticateUserFail = (
  errorMessage = 'Failed to authenticate'
): Thunk<Promise<null>> => async dispatch => {
  Logger.error('Failed to authenticate user', errorMessage);

  removeTokens();
  dispatch({ type: AUTHENTICATE_USER_FAIL, error: new Error(errorMessage) });
  dispatch(setBlockRoute(null));
  return null;
};

export const REFRESH_TOKEN_FAIL = 'AUTH.REFRESH_TOKEN_FAIL';

export const REFRESH_DFD_TOKEN_SUCCESS = 'AUTH.REFRESH_DFD_TOKEN_SUCCESS';
const refreshDfdTokenSuccess = (auth: AuthResult) => ({
  type: REFRESH_DFD_TOKEN_SUCCESS,
  payload: auth
});

/**
 * Refresh tokens
 * this method doesn't throw exception but result can be null
 */
export const refreshToken = async (
  dispatch: Dispatch<AnyAction>,
  state: RootState,
  storedAuth?: StoredAuthInfo
) => {
  let dfdAuth: Partial<AuthResult>;
  const dfdAuthService = getAuthService();
  try {
    const dfdRefreshToken = dfdRefreshTokenSelector(state) || oc(storedAuth).dfdRefreshToken('');

    if (dfdRefreshToken) {
      dfdAuth = await dfdAuthService.refreshToken(dfdRefreshToken);
    }

    if (dfdAuth) {
      dfdAuth = { ...dfdAuth, refreshToken: dfdRefreshToken };
      dispatch(refreshDfdTokenSuccess(dfdAuth as AuthResult));
    }

    return dfdAuth ? { dfdAuth } : null;
  } catch (err) {
    dispatch({ type: REFRESH_TOKEN_FAIL });
    Logger.error('Failed to refresh token', err);
    return null;
  }
};

export const authenticateTPSWithAuth = (
  token: string,
  apiKey: string
): Thunk<Promise<AuthResults | null>> => async (dispatch: any) => {
  const results = await authenticateTPS(
    {
      token,
      apiKey
    },
    true
  );
  return dispatch(setAuthStatusTPS(results));
};

/**
 * Authenticate User through refresh token or login
 */
export const authenticateUser = (
  storedAuth?: StoredAuthInfo | null
): Thunk<Promise<AuthResults | null>> => async (
  dispatch,
  getState
): Promise<AuthResults | null> => {
  dispatch({ type: AUTHENTICATE_USER });
  let auth: AuthResults | null = null;
  const apiKey = apiKeySelector(getState());
  const dfdAuthService = getAuthService();

  // TODO: restore stored auth functionality

  // Check if lastLogin is after configured days and force user to re-login
  storedAuth = storedAuth || (((await getStoredAuthInfo()) as unknown) as StoredAuthInfo); // only get from store throw biometric

  if (storedAuth && !shouldForceLogin(storedAuth.lastLogin)) {
    auth = await refreshToken(dispatch, getState(), storedAuth);
  }
  // Successfully authenticate DFD from refresh token
  if (auth && auth.dfdAuth) {
    dispatch(authenticateUserSuccess(auth));
    dispatch(authenticateTPSWithAuth(auth.dfdAuth.accessToken, apiKey));

    analyticsService.logEvent(AnalyticsEvent.SignInCompleted, {
      application: 'DFD'
    });
    return auth;
  }

  try {
    dfdAuthService.authenticateUser(); // In web we totally change urls and have a redirect url. There is no reason to handle anything byond here.
  } catch (error) {
    dispatch(authenticateUserFail(error.message));
  }
  return null;
};

/**
 * @deprecated since version 5.11.0
 */
export const isAuthenticatedWithTPSSource = (
  token: string,
  source: string
): Thunk<Promise<boolean | null>> => async (dispatch: any, getState: any) => {
  try {
    const state = getState();
    const apiKey = apiKeySelector(state);

    if (!token) {
      throw new Error('No token available when checking if authenticated with TPS source');
    }

    return isAuthenticatedWithSource({ token, apiKey, source: { id: source } });
  } catch (error) {
    return dispatch(authenticateUserFail(error.message));
  }
};

export const AUTHENTICATE_LOGOUT = 'AUTHENTICATE_LOGOUT';
export const logout = () => async (dispatch: Dispatch<AnyAction>, getState: () => RootState) => {
  const dfdAuthService = getAuthService();
  try {
    const dfdRefreshToken = dfdRefreshTokenSelector(getState());
    if (dfdRefreshToken) {
      await dfdAuthService.revokeRefreshToken(dfdRefreshToken);
    }
  } catch (error) {
    Logger.error('Failed to revoke DFD refresh token', error);
  }

  try {
    removeTokens();
  } catch (error) {
    Logger.error('Failed to reset refresh Token', error);
  }

  dispatch({ type: AUTHENTICATE_LOGOUT });
  const redirectUrl = `${window.location.protocol}//${window.location.host}/landing`;
  window.location.href = `${Config.AUTH_SERVER_BASE_URL}/logout?target=${redirectUrl}`;
};

export const AUTHENTICATE_RESET_ACCESS_TOKEN = 'AUTHENTICATE_RESET_ACCESS_TOKEN';
export const resetAccessToken = () => {
  removeTokens();
  return {
    type: AUTHENTICATE_RESET_ACCESS_TOKEN
  };
};

export const navigateToProtectedRoute = (routeName: string) => (
  dispatch: Dispatch<AnyAction>,
  getState: () => RootState
) => {
  const isAuthenticated = isDfdAuthenticatedSelector(getState());
  if (isAuthenticated) {
    dispatch(push(routeName));
  } else {
    window.sessionStorage.setItem('nextPath', routeName);
    dispatch(toggleProtectedRouteModal());
  }
};

export const FORGOT_USERNAME = 'FORGOT_USERNAME';
/**
 * Method: forgotUsername
 * Description: Redirect action for forgotUsername
 */
export const forgotUsername = () => {
  const dfdAuthService = getAuthService();
  dfdAuthService.forgotUsername();
  return { type: FORGOT_USERNAME };
};

export * from './actions.common';
